import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAppContext } from '../../../webapp/src/Providers/AppProvider/AppContext';
import {
  captureAnalyticsMutation,
  captureDBAnalyticsMutation
} from '../../services/react-query/mutation';

import { AnalyticsAppType, AnalyticsModules } from './enums';
import {
  RfqAnalyticsApi,
  CatalogAnalyticsApi,
  ProductMatchAnalyticsApi,
  SalesEnablementAnalyticsApi
} from '../../enums';

const getApi = (module: AnalyticsModules): string => {
  switch (module) {
    case AnalyticsModules.ProductMatch:
      return ProductMatchAnalyticsApi.createAnalytic;
    case AnalyticsModules.AutoRFQList:
    case AnalyticsModules.AutoRFQCreate:
    case AnalyticsModules.AutoRFQEdit:
    case AnalyticsModules.AutoRFQItem:
    case AnalyticsModules.AutoRFQCatalog:
    case AnalyticsModules.AutoRFQProductMatch:
    case AnalyticsModules.AutoRFQDetails:
      return RfqAnalyticsApi.createAnalytic;
    case AnalyticsModules.SalesEnablement:
      return SalesEnablementAnalyticsApi.createAnalytic;
    default:
      return CatalogAnalyticsApi.createAnalytic;
  }
};

export const useAnalytics = () => {
  const { user } = useAppContext();
  const { mutateAsync: saveAnalytics } = useMutation(
    captureAnalyticsMutation()
  );

  const captureAnalytics = async (
    data: { [key: string]: any },
    module: AnalyticsModules
  ) => {
    const catalogBodyProperties = {
      module
    };

    const ignoreModule =
      module !== AnalyticsModules.ProductMatch &&
      module !== AnalyticsModules.SalesEnablement;

    const body = {
      companyId: user.CompanyID,
      userId: user.ID,
      timestamp: new Date().toISOString(),
      ...(ignoreModule && catalogBodyProperties),
      ...data,
      appType: data.appType || AnalyticsAppType.WebApp
    };

    const res = await saveAnalytics({ body, endpoint: getApi(module) });
    return res;
  };

  return { captureAnalytics };
};

export enum DBAnalyticsTabNames {
  OrderHealth = 'orderHealth',
  Orders = 'orders',
  Backorders = 'backorders',
  Invoices = 'invoices',
  RetentionIntelligence = 'retentionIntelligence'
}

export enum DBEventType {
  OrdersModule = 'ordersModule',
  OrderDetails = 'orderDetails',
  OrderCreate = 'orderCreate',
  BackorderDetails = 'backorderDetails',
  BackorderOrderDetails = 'backorderOrderDetails',
  DigitalRoomsRFQ = 'digitalRoomsRFQ',
  InvoiceDetails = 'invoiceDetails',
  ProductDetails = 'productDetails',
  Export = 'export',

  QuotesModule = 'quotesModule',
  QuoteDetails = 'quoteDetails',

  AutoRFQModule = 'autoRFQModule'
}

export const useDatabricksAnalytics = () => {
  const { user } = useAppContext();
  const { mutateAsync } = useMutation(captureDBAnalyticsMutation());

  const captureDBAnalytics = useCallback(
    ({
      eventType,
      data
    }: {
      eventType: string;
      data: { [key: string]: any };
    }) => {
      const body = {
        eventType,
        eventData: {
          appType: AnalyticsAppType.WebApp,
          companyId: user.CompanyID,
          userId: user.ID,
          ...data
        }
      };
      mutateAsync(body);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return { captureDBAnalytics };
};
